import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  message,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
  Row,
  Col,
  AutoComplete
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getProjectGroupList } from '../../../services/project';
import type { ProjectGroup } from '../../../types/project';
import { useAuth } from '../../../hooks/useAuth';
import { useTableQuery } from '../../../hooks/useTableQuery';
import { getProjectList } from '../../../services/project'; // Added import for getProjectList
import type { Project } from '../../../types/project'; // Added import for Project type

const { Search } = Input;
const { Title } = Typography;

// 定义筛选条件类型
interface ProjectGroupFilters {
  search: string;
  project: string; // 新增：包含项目筛选
}

const ProjectGroupList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // 新增：项目列表状态
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  // 实时搜索：项目选项与输入值
  const [projectOptions, setProjectOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [projectInputValue, setProjectInputValue] = useState<string>('');

  // 新增：加载项目列表
  const loadProjects = async () => {
    try {
      setProjectsLoading(true);
      // 分页拉取，直至加载全部项目
      const pageSize = 200;
      let page = 1;
      let total = 0;
      const all: Project[] = [];

      // 先拉取第一页以获得总数
      let resp = await getProjectList({ page, page_size: pageSize });
      total = resp.data?.count || 0;
      all.push(...(resp.data?.results || []));

      // 继续拉取后续页面
      const totalPages = Math.ceil(total / pageSize);
      while (page < totalPages) {
        page += 1;
        // eslint-disable-next-line no-await-in-loop
        resp = await getProjectList({ page, page_size: pageSize });
        all.push(...(resp.data?.results || []));
      }

      setProjects(all);
      // 初次加载即填充候选项（去重）
      const initialOptions = all.map((p: Project) => ({
        value: String(p.id),
        label: `${p.project_code} - ${p.name || '未命名项目'}`
      }));
      setProjectOptions(initialOptions);
    } catch (error) {
    } finally {
      setProjectsLoading(false);
    }
  };

  // 新增：初始化加载项目列表
  useEffect(() => {
    loadProjects();
  }, []);

  // 实时搜索项目（用于 AutoComplete）
  const searchProjects = async (keyword: string) => {
    try {
      const resp = await getProjectList({ page_size: 20, search: keyword || '' });
      const results: Project[] = resp.data?.results || [];
      setProjectOptions(results.map(p => ({
        value: String(p.id),
        label: `${p.project_code} - ${p.name || '未命名项目'}`
      })));
    } catch (e) {
      setProjectOptions([]);
    }
  };

  // 使用 useTableQuery Hook - 实现"单一数据源 + 强制同步 + 解耦触发"架构
  const {
    state,
    data,
    loading,
    error,
    actions,
    paginationConfig
  } = useTableQuery<ProjectGroupFilters>({
    initialState: {
      pagination: { current: 1, pageSize: 10, total: 0 },
      filters: { search: '', project: '' },
      sorting: { field: 'created_at', order: 'desc' }
    },
    urlConfig: {
      syncToUrl: true,           // 启用URL状态同步
      urlKeys: ['pagination', 'filters', 'sorting']  // 同步分页、筛选、排序到URL
    },
    fetchData: async (state) => {
      const { pagination, filters, sorting } = state;

      // 构建API参数
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        ordering: sorting.order === 'desc' ? `-${sorting.field}` : sorting.field,
        search: filters.search || '',
        project: filters.project || '' // 添加项目筛选参数
      };
      
      // 调用API
      const response = await getProjectGroupList(params);

      return {
        results: response.data.results,
        count: response.data.count
      };
    }
  });

  // 若 URL 中已有项目筛选，则在初次加载后将输入框显示为对应项目名
  useEffect(() => {
    if (state?.filters?.project) {
      const match = projects.find(p => String(p.id) === String(state.filters.project));
      if (match) {
        setProjectInputValue(`${match.project_code} - ${match.name || '未命名项目'}`);
      }
    } else {
      setProjectInputValue('');
    }
  }, [state?.filters?.project, projects]);

  // 处理筛选条件变化
  const handleFilterChange = (key: keyof ProjectGroupFilters, value: any) => {
    // 更新筛选条件
    const newFilters = { [key]: value };

    // 更新状态
    actions.updateFilters(newFilters);
    
    // 搜索条件或项目筛选条件变化时，重置到第一页
    if (key === 'search' || key === 'project') {
      actions.updatePagination({ current: 1 });
    }
  };

  // 处理搜索（点击搜索按钮时调用）
  const handleSearch = () => {
    // 搜索时重置到第一页
    actions.updatePagination({ current: 1 });
  };

  // 处理搜索框输入变化（实时搜索，带防抖）
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // 更新搜索条件
    handleFilterChange('search', value);
  };

  // 重置筛选条件
  const handleReset = () => {
    actions.reset();
  };

  // 表格变化处理（分页、排序等）
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {

    // 处理分页变化
    if (pagination.current !== state.pagination.current) {
      actions.updatePagination({ current: pagination.current });
    }
    
    if (pagination.pageSize !== state.pagination.pageSize) {
      actions.updatePagination({ current: 1, pageSize: pagination.pageSize });
    }
    
    // 处理排序变化
    if (sorter && sorter.field) {
      actions.updateSorting({
        field: sorter.field,
        order: sorter.order === 'ascend' ? 'asc' : 'desc'
      });
    }
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    try {
      // TODO: 实现删除项目组的 API 调用
      message.success('删除成功');
      // 重新获取数据
      actions.refresh();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '删除项目组失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<ProjectGroup> = [
    {
      title: '项目组名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record) => (
        <a onClick={() => navigate(`/dashboard/project-groups/${record.id}`)}>
          {text}
        </a>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: {
        showTitle: false,
      },
      render: (remarks: string) => (
        <Tooltip placement="topLeft" title={remarks}>
          {remarks || '-'}
        </Tooltip>
      ),
    },
    {
      title: '所含项目数量',
      key: 'projects',
      render: (_, record) => {
        // 显示项目组包含的所有项目
        const projectCount = projects.filter(p => p.project_group?.id === record.id).length;
        
        if (projectCount === 0) {
          return <Tag color="default">无项目</Tag>;
        }
        
        return (
          <Tooltip 
            title={
              <div>
                <div>包含项目：</div>
                {projects
                  .filter(p => p.project_group?.id === record.id)
                  .map(project => (
                    <div key={project.id}>
                      {project.project_code} - {project.name || '未命名项目'}
                    </div>
                  ))
                }
              </div>
            }
            placement="topLeft"
          >
            <Tag color="blue" style={{ cursor: 'pointer' }}>
              {projectCount} 个项目
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="功能开发中">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => message.info('功能开发中')}
            >
              查看
            </Button>
          </Tooltip>
          {(user?.role === 'customer' && record.owner.id === user.id) && (
            <>
              <Tooltip title="功能开发中">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => message.info('功能开发中')}
                >
                  编辑
                </Button>
              </Tooltip>
              <Tooltip title="功能开发中">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled
                >
                  删除
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="project-group-list">
      <Card>
        {/* 页面标题和统计信息 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Title level={4} style={{ margin: 0 }}>项目组列表</Title>
              <div className="flex items-center space-x-2">
                <Tag color="blue" style={{ fontSize: '14px', padding: '8px' }}>
                  共 {state.pagination.total} 条记录
                </Tag>
              </div>
            </div>
            
            {/* 右侧操作按钮 */}
            <div className="flex items-center space-x-2">
              {user?.role === 'customer' && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/dashboard/customer/project-groups/create')}
                >
                  创建项目组
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* 筛选区域 */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <Space>
                <span style={{ fontWeight: 500, color: '#333' }}>搜索：</span>
                <Input
                  placeholder="搜索项目组"
                  value={state.filters.search}
                  onChange={handleSearchInputChange}
                  allowClear
                  style={{ width: 200 }}
                />
              </Space>
            </Col>
            <Col span={6}>
              <Space>
                <span style={{ fontWeight: 500, color: '#333' }}>所含项目：</span>
                <AutoComplete
                  placeholder="输入项目编码/名称，回车选择"
                  value={projectInputValue}
                  options={projectOptions}
                  style={{ width: 260 }}
                  allowClear
                  onSearch={(val) => {
                    setProjectInputValue(val);
                    searchProjects(val);
                  }}
                  onChange={(val) => setProjectInputValue(val)}
                  onSelect={(value, option) => {
                    setProjectInputValue(option.label as string);
                    handleFilterChange('project', value);
                  }}
                  onClear={() => {
                    setProjectInputValue('');
                    handleFilterChange('project', '');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (projectOptions && projectOptions.length > 0) {
                        const first = projectOptions[0];
                        setProjectInputValue(first.label);
                        handleFilterChange('project', first.value);
                      }
                    }
                  }}
                  notFoundContent={projectsLoading ? '加载中...' : '无匹配项目'}
                />
              </Space>
            </Col>
            <Col span={6}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  搜索
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                >
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={paginationConfig}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default ProjectGroupList; 